/**
 * useEditorMode Hook
 * 
 * Manages editor mode state and keyboard toggle.
 * Syncs editor mode with GameScene.
 */

import { useState, useEffect } from 'react';
import { DevConfig } from '../../config/environment/devConfig';
import { GameScene } from '../scenes/GameScene';

interface UseEditorModeProps {
  gameWorld: GameScene | null;
}

interface UseEditorModeReturn {
  isEditorMode: boolean;
  setIsEditorMode: (value: boolean) => void;
  toggleEditor: () => void;
}

export const useEditorMode = ({ gameWorld }: UseEditorModeProps): UseEditorModeReturn => {
  const [isEditorMode, setIsEditorMode] = useState(DevConfig.editorOverlayOnByDefault);

  // NOTE: the Backtick (Backquote) keyboard toggle was removed (2026-06-09) —
  // it crashed the client when flipped on a live gameplay map. The editor is now
  // toggled exclusively through the UI (DevTools Editor tab → editor:manual-*
  // events below), so backtick is a complete no-op.

  // Manual enable path (bypasses DevConfig.systems.editor gate). Used by the
  // "Enable Editor Mode" button in the DevTools Editor tab so DJ can flip the
  // editor on from the UI for ad-hoc multiplayer / boot-preset testing even
  // when the current preset has the editor system disabled.
  //
  // Event-based on purpose: keeps this hook decoupled from the dispatcher
  // (GameUIOverlay handles the action) and doesn't add a second keydown path.
  useEffect(() => {
    const handleManualEnable = () => setIsEditorMode(true);
    const handleManualDisable = () => setIsEditorMode(false);
    const handleManualToggle = () => setIsEditorMode(prev => !prev);
    window.addEventListener('editor:manual-enable', handleManualEnable);
    window.addEventListener('editor:manual-disable', handleManualDisable);
    window.addEventListener('editor:manual-toggle', handleManualToggle);
    return () => {
      window.removeEventListener('editor:manual-enable', handleManualEnable);
      window.removeEventListener('editor:manual-disable', handleManualDisable);
      window.removeEventListener('editor:manual-toggle', handleManualToggle);
    };
  }, []);

  // Sync editor mode with GameScene
  useEffect(() => {
    if (gameWorld) {
      gameWorld.setEditorMode(isEditorMode);
    }
  }, [isEditorMode, gameWorld]);

  const toggleEditor = () => setIsEditorMode(prev => !prev);

  return {
    isEditorMode,
    setIsEditorMode,
    toggleEditor
  };
};

export default useEditorMode;
